import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Save, Power, Clock, MessageSquareOff, AlertCircle } from "lucide-react";

function Toggle({ checked, onChange, disabled, testId }) {
    return (
        <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            disabled={disabled}
            data-testid={testId}
            className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                checked ? "bg-brand" : "bg-slate-300"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
            <span
                className={`pointer-events-none inline-block h-6 w-6 translate-x-0 rounded-full bg-white shadow ring-0 transition duration-200 ${
                    checked ? "translate-x-5" : "translate-x-0"
                }`}
            />
        </button>
    );
}

export default function Settings() {
    const { user } = useAuth();
    const canEdit = user?.role === "chairman" || user?.role === "head";

    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/settings");
            setData(data);
            setForm({
                appeals_enabled: data.appeals_enabled,
                use_working_hours: data.use_working_hours,
                hour_start: data.hour_start,
                hour_end: data.hour_end,
                timezone: data.timezone,
                off_hours_message: data.off_hours_message || "",
                disabled_message: data.disabled_message || "",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // refresh status every 30s
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, []);

    const save = async (patch) => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const { data: updated } = await api.patch("/settings", patch || form);
            setData(updated);
            setForm({
                appeals_enabled: updated.appeals_enabled,
                use_working_hours: updated.use_working_hours,
                hour_start: updated.hour_start,
                hour_end: updated.hour_end,
                timezone: updated.timezone,
                off_hours_message: updated.off_hours_message || "",
                disabled_message: updated.disabled_message || "",
            });
            setSuccess("Изменения сохранены");
            setTimeout(() => setSuccess(""), 2500);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const quickToggleAppeals = async () => {
        if (!canEdit) return;
        await save({ appeals_enabled: !form.appeals_enabled });
    };

    if (loading || !form) {
        return (
            <div className="p-4 lg:p-10" data-testid="settings-page">
                <div className="text-sm text-slate-500">Загрузка...</div>
            </div>
        );
    }

    const accepting = data?.status?.accepting;
    const reason = data?.status?.reason;
    const localTime = data?.status?.current_local_time;

    return (
        <div className="p-4 lg:p-10 max-w-4xl" data-testid="settings-page">
            <header className="mb-6 lg:mb-8">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1 lg:mb-2 hidden lg:block">
                    Настройки
                </div>
                <h1 className="text-2xl lg:text-4xl font-heading font-medium tracking-tight text-slate-900">
                    Настройки приёмной комиссии
                </h1>
                <p className="text-slate-500 mt-1 lg:mt-2 text-sm lg:text-base">
                    Управление приёмом обращений и часами работы
                </p>
            </header>

            {/* STATUS BANNER */}
            <div
                className={`mb-5 lg:mb-6 rounded-lg border p-4 lg:p-5 flex items-center gap-4 ${
                    accepting
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-amber-50 border-amber-200"
                }`}
                data-testid="settings-status-banner"
            >
                <div
                    className={`h-12 w-12 rounded-full flex items-center justify-center shrink-0 ${
                        accepting ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}
                >
                    {accepting ? <Power className="h-5 w-5" /> : <MessageSquareOff className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading font-medium text-slate-900">
                        {accepting
                            ? "Сейчас обращения принимаются"
                            : reason === "disabled"
                            ? "Приём обращений выключен вручную"
                            : "Сейчас вне рабочих часов"}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">
                        {accepting
                            ? "Бот принимает новые обращения от абитуриентов и пересылает их в кабинет."
                            : "Бот показывает абитуриентам уведомление о том, что приёмная комиссия сейчас не отвечает. Меню с информацией работает."}
                        {localTime && (
                            <span className="ml-2 text-slate-500">
                                · сейчас {localTime} {data.timezone === "Europe/Minsk" ? "(Минск)" : ""}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* MASTER TOGGLE */}
            <section className="bg-white border border-slate-200 rounded-lg p-5 lg:p-6 mb-4 lg:mb-5" data-testid="settings-master-card">
                <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                        <Power className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-4 mb-1">
                            <h3 className="font-heading font-medium text-slate-900">
                                Принимать обращения
                            </h3>
                            <Toggle
                                checked={form.appeals_enabled}
                                onChange={(v) => save({ appeals_enabled: v })}
                                disabled={!canEdit || saving}
                                testId="settings-toggle-appeals"
                            />
                        </div>
                        <p className="text-sm text-slate-500">
                            Главный переключатель. Выключите, чтобы временно остановить приём
                            обращений (например, во время праздников). Меню с информацией о специальностях,
                            документах и контактах продолжит работать.
                        </p>
                    </div>
                </div>
            </section>

            {/* WORKING HOURS */}
            <section className="bg-white border border-slate-200 rounded-lg p-5 lg:p-6 mb-4 lg:mb-5" data-testid="settings-hours-card">
                <div className="flex items-start gap-4 mb-5">
                    <div className="h-10 w-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                        <Clock className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-4 mb-1">
                            <h3 className="font-heading font-medium text-slate-900">Часы работы</h3>
                            <Toggle
                                checked={form.use_working_hours}
                                onChange={(v) => setForm({ ...form, use_working_hours: v })}
                                disabled={!canEdit || saving}
                                testId="settings-toggle-hours"
                            />
                        </div>
                        <p className="text-sm text-slate-500">
                            Принимать обращения только в указанный интервал по местному времени Минска.
                            В остальное время бот покажет уведомление о расписании.
                        </p>
                    </div>
                </div>

                <div
                    className={`grid grid-cols-2 gap-4 ${
                        form.use_working_hours ? "" : "opacity-50 pointer-events-none"
                    }`}
                >
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Начало (час)
                        </label>
                        <input
                            type="number"
                            min={0}
                            max={23}
                            value={form.hour_start}
                            onChange={(e) =>
                                setForm({ ...form, hour_start: Math.max(0, Math.min(23, parseInt(e.target.value || "0", 10))) })
                            }
                            disabled={!canEdit}
                            data-testid="settings-hour-start"
                            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none disabled:bg-slate-50"
                        />
                        <p className="text-xs text-slate-400 mt-1.5">
                            Часы 0–23. Сейчас: {form.hour_start}:00
                        </p>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Конец (час)
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={24}
                            value={form.hour_end}
                            onChange={(e) =>
                                setForm({ ...form, hour_end: Math.max(1, Math.min(24, parseInt(e.target.value || "0", 10))) })
                            }
                            disabled={!canEdit}
                            data-testid="settings-hour-end"
                            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none disabled:bg-slate-50"
                        />
                        <p className="text-xs text-slate-400 mt-1.5">
                            Часы 1–24. Сейчас: {form.hour_end}:00
                        </p>
                    </div>
                </div>
            </section>

            {/* OFF-HOURS MESSAGES */}
            <section className="bg-white border border-slate-200 rounded-lg p-5 lg:p-6 mb-4 lg:mb-5" data-testid="settings-messages-card">
                <h3 className="font-heading font-medium text-slate-900 mb-1">Сообщения для абитуриента</h3>
                <p className="text-sm text-slate-500 mb-5">
                    Что бот ответит, если абитуриент попытается отправить обращение, когда приём выключен.
                </p>

                <div className="space-y-5">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Сообщение вне рабочих часов
                        </label>
                        <textarea
                            value={form.off_hours_message}
                            onChange={(e) => setForm({ ...form, off_hours_message: e.target.value })}
                            rows={4}
                            disabled={!canEdit}
                            data-testid="settings-off-hours-message"
                            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none disabled:bg-slate-50"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            Сообщение при ручном выключении
                        </label>
                        <textarea
                            value={form.disabled_message}
                            onChange={(e) => setForm({ ...form, disabled_message: e.target.value })}
                            rows={4}
                            disabled={!canEdit}
                            data-testid="settings-disabled-message"
                            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none disabled:bg-slate-50"
                        />
                    </div>
                </div>
            </section>

            {error && (
                <div className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 flex items-start gap-2" data-testid="settings-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
            {success && (
                <div className="mb-4 text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md px-4 py-3" data-testid="settings-success">
                    {success}
                </div>
            )}

            {canEdit && (
                <div className="flex justify-end gap-3">
                    <button
                        onClick={() => save()}
                        disabled={saving}
                        data-testid="settings-save"
                        className="inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-hover disabled:opacity-60 transition-colors rounded-md px-5 py-2.5 font-medium text-sm"
                    >
                        <Save className="h-4 w-4" />
                        {saving ? "Сохранение..." : "Сохранить настройки"}
                    </button>
                </div>
            )}
        </div>
    );
}
